import TransportDocumentLinks from '../modules/transports/components/TransportDocumentLinks';
import TransportDeliveryConfirmationModal, { type TransportDeliveryConfirmation } from '../modules/transports/components/TransportDeliveryConfirmationModal';
import { submitWithOperationalException } from '../modules/operationalExceptions/operationalExceptionFlow';
import { buildTransportOrderRouteLabel } from '../modules/transports/transportOrderRouteLabel';
import { useDuplicateTransport } from '../modules/transports/useDuplicateTransport';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  App,
  Button,
  Dropdown,
  Empty,
  Input,
  Modal,
  Pagination,
  Progress,
  Segmented,
  Select,
  Spin,
  Table,
  Tooltip,
} from 'antd';
import type { MenuProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CalendarOutlined,
  CarOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  CopyOutlined,
  EditOutlined,
  EyeOutlined,
  NodeIndexOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
  RightOutlined,
  SearchOutlined,
  StopOutlined,
  TableOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { returnToCurrentScreen, withTransportReturnContext } from '../modules/transports/transportReturnContext';
import { useTranslation } from 'react-i18next';
import './TransportRoutes.css';
import type {
  TransportRouteDetailDto,
  TransportProductDto,
  TransportRouteStatus,
  TransportRouteSummaryDto,
} from '../dto/transport';
import {
  fetchTransportRoute,
  updateTransportRouteAndRefetch,
  deleteTransportRoute,
  listTransportRoutes,
  updateTransportRoute,
  type TransportRouteListFilters,
} from '../modules/transports/api/transportRoutesApi';
import {
  buildTransportOrderGrouping,
  type TransportOrderGroup,
  type TransportOrderTruck,
} from '../modules/transports/transportOrderGroups';
import {
  detectTransportStatusView,
  getTransportStatusDisplay,
  getTransportStatusDisplayTranslationKey,
  TRANSPORT_STATUS_VIEW_GROUPS,
  type TransportStatusView,
} from '../modules/transports/transportRouteViews';
import {
  getTransportErrorMessage,
  isBlockingTransportError,
} from '../modules/transports/transportErrorMessages';
import {
  canDeleteTransportRoute,
  canManageTransportRoutes,
} from '../modules/transports/transportRouteAccessPolicy';
import {
  buildTransportOnlyRouteSections,
  buildTransportRouteSections,
  type TransportRouteSection,
} from '../modules/transports/transportRouteSections';
import { buildTransportRouteLabel } from '../modules/orders/transportAddressUtils';
import {
  getOrderProductDetailDisplayName,
  getOrderProductDisplayDimensions,
  getOrderProductVariantLabels,
} from '../modules/orders/orderProductDisplay';
import { authService } from '../services/auth';
import { usePageAutoRefresh } from '../hooks/usePageAutoRefresh';
import { formatMoneyPl } from '../utils/currency';
import { formatDateEuropean } from '../utils/dateUtils';
import { useFeedback } from '../utils/feedback';
import { formatCargoQuantityPair, formatCargoQuantityValue } from '../utils/cargoQuantity';
import { formatLocalizedPercent } from '../utils/numberFormatting';
import { formatUnit } from '../utils/units';
import { getTransportLabel } from '../utils/transportLabel';

interface ProductPresentation {
  key: string;
  name: string;
  quantity: string | null;
  dimensions: string | null;
  labels: string[];
}

type TransportSortMode =
  | 'source'
  | 'orderNumber'
  | 'orderNumberDescending'
  | 'customer'
  | 'customerDescending'
  | 'loadingNewest'
  | 'loadingOldest'
  | 'carrier'
  | 'carrierDescending'
  | 'rateHighest'
  | 'rateLowest'
  | 'status'
  | 'statusDescending';

function compareNullableDates(left?: string, right?: string): number {
  const leftValue = left && dayjs(left).isValid() ? dayjs(left).valueOf() : 0;
  const rightValue = right && dayjs(right).isValid() ? dayjs(right).valueOf() : 0;
  return leftValue - rightValue;
}

function compareTransports(
  left: TransportRouteSummaryDto | undefined,
  right: TransportRouteSummaryDto | undefined,
  sortMode: TransportSortMode,
): number {
  if (!left && !right) return 0;
  if (!left) return 1;
  if (!right) return -1;
  if (sortMode === 'loadingNewest') return compareNullableDates(right.loadingDate, left.loadingDate);
  if (sortMode === 'loadingOldest') return compareNullableDates(left.loadingDate, right.loadingDate);
  if (sortMode === 'carrier') return left.transportCompany.localeCompare(right.transportCompany, 'pl');
  if (sortMode === 'carrierDescending') return right.transportCompany.localeCompare(left.transportCompany, 'pl');
  if (sortMode === 'rateHighest') {
    return (Number(right.transportRate) || 0) - (Number(left.transportRate) || 0);
  }
  if (sortMode === 'rateLowest') {
    return (Number(left.transportRate) || 0) - (Number(right.transportRate) || 0);
  }
  if (sortMode === 'status') return left.status.localeCompare(right.status, 'pl');
  if (sortMode === 'statusDescending') return right.status.localeCompare(left.status, 'pl');
  return 0;
}

function sortOrderGroups(
  groups: TransportOrderGroup[],
  sortMode: TransportSortMode,
): TransportOrderGroup[] {
  const sortedGroups = groups.map((group) => ({
    ...group,
    trucks: sortMode === 'source'
      ? group.trucks
      : [...group.trucks].sort((left, right) => (
        compareTransports(left.transport, right.transport, sortMode)
      )),
  }));

  if (sortMode === 'source') return sortedGroups;
  return sortedGroups.sort((left, right) => {
    if (sortMode === 'orderNumber' || sortMode === 'orderNumberDescending') {
      const leftNumber = left.order.displayOrderNumber || left.order.orderNumber;
      const rightNumber = right.order.displayOrderNumber || right.order.orderNumber;
      const comparison = leftNumber.localeCompare(rightNumber, 'pl', { numeric: true });
      return sortMode === 'orderNumberDescending' ? -comparison : comparison;
    }
    if (sortMode === 'customer' || sortMode === 'customerDescending') {
      const leftCustomer = left.order.customer?.name || left.order.customerName || '';
      const rightCustomer = right.order.customer?.name || right.order.customerName || '';
      const comparison = leftCustomer.localeCompare(rightCustomer, 'pl');
      return sortMode === 'customerDescending' ? -comparison : comparison;
    }
    return compareTransports(
      left.trucks[0]?.transport,
      right.trucks[0]?.transport,
      sortMode,
    );
  });
}

function parseStatusFilter(value: string | null): string[] {
  return value
    ? value.split(',').map((entry) => entry.trim()).filter(Boolean)
    : [];
}

function buildLicensePlates(transport: TransportRouteSummaryDto): string | null {
  const value = [transport.licensePlate1, transport.licensePlate2, transport.vehicleRegistration]
    .map((plate) => plate?.trim())
    .filter((plate, index, values): plate is string => Boolean(plate) && values.indexOf(plate) === index)
    .join(' / ');
  return value || null;
}

function statusIcon(status: TransportRouteStatus): React.ReactNode {
  switch (getTransportStatusDisplay(status)) {
    case 'planned': return <CalendarOutlined />;
    case 'inProgress': return <CarOutlined />;
    case 'completed': return <CheckCircleOutlined />;
    default: return <StopOutlined />;
  }
}

const QUICK_STATUS_OPTIONS: TransportRouteStatus[] = [
  'SCHEDULED',
  'IN_TRANSIT',
  'DELIVERED',
];

function presentProduct(
  product: TransportProductDto,
  basicProductLabel: string,
  language: string,
  fallbackKey: string,
): ProductPresentation {
  const name = getOrderProductDetailDisplayName(product, product.name || basicProductLabel);
  const quantity = product.quantity == null
    ? null
    : formatCargoQuantityPair(product, language);

  return {
    key: product.cargoItemId || product.id || fallbackKey,
    name,
    quantity,
    dimensions: getOrderProductDisplayDimensions(product) || null,
    labels: getOrderProductVariantLabels(product, basicProductLabel)
      .map((label) => label.key ? `${label.key}: ${label.value}` : label.value),
  };
}

const TransportRoutes: React.FC = () => {
  const { t, i18n } = useTranslation(['transport', 'common', 'products']);
  const feedback = useFeedback();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [transports, setTransports] = useState<TransportRouteSummaryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<string[]>(
    () => parseStatusFilter(searchParams.get('status')),
  );
  const [expandedOrderIds, setExpandedOrderIds] = useState<Set<string>>(new Set());
  // Route sections start collapsed (same as order groups, truck details and
  // the supplier-only section) — expanding is an explicit operator action.
  const [expandedRouteKeys, setExpandedRouteKeys] = useState<Set<string>>(new Set());
  const [expandedTruckKeys, setExpandedTruckKeys] = useState<Set<string>>(new Set());
  const [supplierOnlyExpanded, setSupplierOnlyExpanded] = useState(false);
  // Excel-style dense table next to the grouped view (operator feedback:
  // Valentyna wants every transport visible at a glance). Persisted per
  // browser; defaults to the grouped view.
  type TransportListView = 'grouped' | 'compact';
  const [listView, setListView] = useState<TransportListView>(() => (
    window.localStorage.getItem('transport-routes-list-view') === 'compact' ? 'compact' : 'grouped'
  ));
  const [compactPage, setCompactPage] = useState(1);
  const [sortMode, setSortMode] = useState<TransportSortMode>('source');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [supplierOnlyPage, setSupplierOnlyPage] = useState(1);
  const [pendingDelete, setPendingDelete] = useState<TransportRouteSummaryDto | null>(null);
  const { modal } = App.useApp();
  const [deliveryTransport, setDeliveryTransport] = useState<TransportRouteDetailDto | null>(null);
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  const fetchSequenceRef = useRef(0);

  const currentUser = authService.getCurrentUser();
  const canManageTransports = canManageTransportRoutes(currentUser);
  const location = useLocation();
  // Detail/edit/new pages come back to this exact view (status tab, search)
  // instead of the default list — see transportReturnContext.ts.
  const returnContext = returnToCurrentScreen(location);
  const { duplicate, duplicating } = useDuplicateTransport(returnContext);
  const canDeleteTransports = canDeleteTransportRoute(currentUser);
  const canViewTransportCosts = authService.canViewTransportCosts();
  const currentStatusView = detectTransportStatusView(statusFilter);
  const grouping = useMemo(() => buildTransportOrderGrouping(transports), [transports]);
  const sortedOrderGroups = useMemo(
    () => sortOrderGroups(grouping.orderGroups, sortMode),
    [grouping.orderGroups, sortMode],
  );
  const sortedSupplierOnlyTransports = useMemo(() => (
    sortMode === 'source'
      ? grouping.supplierOnlyTransports
      : [...grouping.supplierOnlyTransports].sort((left, right) => (
        compareTransports(left, right, sortMode)
      ))
  ), [grouping.supplierOnlyTransports, sortMode]);
  const visibleOrderGroups = useMemo(() => {
    const offset = (currentPage - 1) * pageSize;
    return sortedOrderGroups.slice(offset, offset + pageSize);
  }, [currentPage, pageSize, sortedOrderGroups]);
  const visibleSupplierOnlyTransports = useMemo(() => {
    const offset = (supplierOnlyPage - 1) * pageSize;
    return sortedSupplierOnlyTransports.slice(offset, offset + pageSize);
  }, [pageSize, sortedSupplierOnlyTransports, supplierOnlyPage]);
  const visibleRouteSections = useMemo(
    () => buildTransportRouteSections(visibleOrderGroups),
    [visibleOrderGroups],
  );
  const visibleSupplierOnlyRouteSections = useMemo(
    () => buildTransportOnlyRouteSections(visibleSupplierOnlyTransports),
    [visibleSupplierOnlyTransports],
  );
  // Flat, page-sorted source for the compact table: same sortMode as the
  // grouped view, order/customer modes fall back to the first linked order.
  const sortedTransports = useMemo(() => {
    if (sortMode === 'source') return transports;
    const firstOrderNumber = (transport: TransportRouteSummaryDto): string => {
      const numbers = transport.routeOrders.map((routeOrder) => (
        routeOrder.order.displayOrderNumber || routeOrder.order.orderNumber
      ));
      return numbers.sort((left, right) => left.localeCompare(right, 'pl', { numeric: true }))[0]
        || transport.displayOrderNumber
        || transport.orderNumber
        || '';
    };
    const firstCustomer = (transport: TransportRouteSummaryDto): string => (
      transport.routeOrders
        .map((routeOrder) => routeOrder.order.customer?.name || routeOrder.order.customerName || '')
        .sort((left, right) => left.localeCompare(right, 'pl'))[0]
      || transport.customerName
      || ''
    );
    return [...transports].sort((left, right) => {
      if (sortMode === 'orderNumber' || sortMode === 'orderNumberDescending') {
        const comparison = firstOrderNumber(left).localeCompare(firstOrderNumber(right), 'pl', { numeric: true });
        return sortMode === 'orderNumberDescending' ? -comparison : comparison;
      }
      if (sortMode === 'customer' || sortMode === 'customerDescending') {
        const comparison = firstCustomer(left).localeCompare(firstCustomer(right), 'pl');
        return sortMode === 'customerDescending' ? -comparison : comparison;
      }
      return compareTransports(left, right, sortMode);
    });
  }, [transports, sortMode]);
  const compactPageItems = useMemo(() => {
    const offset = (compactPage - 1) * pageSize;
    return sortedTransports.slice(offset, offset + pageSize);
  }, [compactPage, pageSize, sortedTransports]);
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(sortedOrderGroups.length / pageSize));
    setCurrentPage((page) => Math.min(page, maxPage));
  }, [pageSize, sortedOrderGroups.length]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(sortedSupplierOnlyTransports.length / pageSize));
    setSupplierOnlyPage((page) => Math.min(page, maxPage));
  }, [pageSize, sortedSupplierOnlyTransports.length]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(sortedTransports.length / pageSize));
    setCompactPage((page) => Math.min(page, maxPage));
  }, [pageSize, sortedTransports.length]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (searchTerm.trim()) next.set('search', searchTerm.trim());
    if (statusFilter.length > 0) next.set('status', statusFilter.join(','));
    setSearchParams(next, { replace: true });
  }, [searchTerm, setSearchParams, statusFilter]);

  useEffect(() => {
    setCurrentPage(1);
    setSupplierOnlyPage(1);
    setCompactPage(1);
  }, [searchTerm, sortMode, statusFilter]);

  const fetchTransports = useCallback(async (): Promise<void> => {
    const sequence = ++fetchSequenceRef.current;
    setLoading(true);

    const filters: TransportRouteListFilters = {};
    if (searchTerm.trim()) filters.search = searchTerm.trim();
    if (statusFilter.length > 0) filters.status = statusFilter.join(',');

    try {
      const result = await listTransportRoutes(filters);
      if (sequence === fetchSequenceRef.current) setTransports(result);
    } catch (_error) {
      if (sequence === fetchSequenceRef.current) {
        feedback.error(t('transport:errorFetchingRoutes'));
      }
    } finally {
      if (sequence === fetchSequenceRef.current) setLoading(false);
    }
  }, [feedback, searchTerm, statusFilter, t]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => void fetchTransports(), 250);
    return () => window.clearTimeout(timeoutId);
  }, [fetchTransports]);

  usePageAutoRefresh(fetchTransports);

  const toggleOrder = (orderId: string): void => {
    setExpandedOrderIds((current) => {
      const next = new Set(current);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const toggleRoute = (routeKey: string): void => {
    setExpandedRouteKeys((current) => {
      const next = new Set(current);
      if (next.has(routeKey)) next.delete(routeKey);
      else next.add(routeKey);
      return next;
    });
  };

  const toggleTruckDetails = (truckKey: string): void => {
    setExpandedTruckKeys((current) => {
      const next = new Set(current);
      if (next.has(truckKey)) next.delete(truckKey);
      else next.add(truckKey);
      return next;
    });
  };

  const handleDelete = async (): Promise<void> => {
    if (!pendingDelete) return;

    try {
      const deletedNumber = getTransportLabel(pendingDelete);
      // WP12 (1.16): linked WZ/PZ ask for a confirmed warning.
      const deleted = await deleteTransportRoute(pendingDelete.id, { modal, t });
      setPendingDelete(null);
      if (!deleted) return;
      feedback.success(t('transport:messages.deleteSuccess', { number: deletedNumber }));
      await fetchTransports();
    } catch (error: any) {
      feedback.error(
        getTransportErrorMessage(error, t, 'transport:messages.deleteError', {
          number: getTransportLabel(pendingDelete),
        }),
        { sticky: isBlockingTransportError(error) },
      );
    }
  };

  const handleQuickStatusChange = async (
    transport: TransportRouteSummaryDto,
    status: TransportRouteStatus,
  ): Promise<void> => {
    if (status === transport.status) return;

    setUpdatingStatusId(transport.id);
    try {
      if (status === 'DELIVERED') {
        const detail = await fetchTransportRoute(transport.id);
        setDeliveryTransport(detail);
        return;
      }
      await updateTransportRoute(transport.id, { status });
      feedback.success(t('transport:messages.statusUpdateSuccess', {
        number: getTransportLabel(transport),
        status: t(getTransportStatusDisplayTranslationKey(status)),
      }));
      await fetchTransports();
    } catch (error: any) {
      feedback.error(
        getTransportErrorMessage(error, t, 'transport:messages.statusUpdateError', {
          number: getTransportLabel(transport),
        }),
        { sticky: isBlockingTransportError(error) },
      );
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const confirmDelivery = async (payload: TransportDeliveryConfirmation): Promise<boolean> => {
    if (!deliveryTransport) return false;
    try {
      const saved = await submitWithOperationalException(
        confirmed => updateTransportRouteAndRefetch(deliveryTransport.id, confirmed),
        { ...payload }, modal, t,
      );
      if (!saved) return false;
      feedback.success(t('transport:messages.statusUpdateSuccess', {
        number: getTransportLabel(saved),
        status: t(getTransportStatusDisplayTranslationKey(saved.status)),
      }));
      setDeliveryTransport(null);
      await fetchTransports();
      return true;
    } catch (error: any) {
      feedback.error(getTransportErrorMessage(error, t, 'transport:messages.statusUpdateError', {
        number: getTransportLabel(deliveryTransport),
      }), { sticky: isBlockingTransportError(error) });
      return false;
    }
  };

  const getRowMenuItems = (record: TransportRouteSummaryDto): MenuProps['items'] => {
    const items: NonNullable<MenuProps['items']> = [{
      key: 'view',
      icon: <EyeOutlined />,
      label: t('transport:menu.view'),
    }];
    if (canManageTransports) {
      items.push({ key: 'edit', icon: <EditOutlined />, label: t('transport:menu.edit') });
      items.push({ key: 'duplicate', icon: <CopyOutlined />, label: t('transport:duplicate.action'), disabled: duplicating });
      items.push({
        key: 'change-status',
        label: t('transport:menu.changeStatus'),
        children: [
          {
            type: 'group',
            label: t('transport:menu.currentStatus'),
            children: [{
              key: 'current-status',
              icon: statusIcon(record.status),
              label: t(getTransportStatusDisplayTranslationKey(record.status)),
              disabled: true,
            }],
          },
          { type: 'divider' },
          {
            type: 'group',
            label: t('transport:menu.changeStatusTo'),
            children: QUICK_STATUS_OPTIONS
              .filter((status) => status !== record.status)
              .map((status) => ({
                key: `status:${status}`,
                icon: statusIcon(status),
                label: t(getTransportStatusDisplayTranslationKey(status)),
              })),
          },
        ],
      });
    }
    if (canDeleteTransports) {
      items.push({
        key: 'delete',
        icon: <DeleteOutlined />,
        label: t('transport:menu.delete'),
        danger: true,
      });
    }
    return items;
  };

  const renderStatus = (transport: TransportRouteSummaryDto): React.ReactNode => {
    const displayStatus = getTransportStatusDisplay(transport.status);
    const label = t(getTransportStatusDisplayTranslationKey(transport.status), {
      defaultValue: t('common:unknownStatus'),
    });
    return (
      <span
        className={`transport-truck-row__status transport-truck-row__status--${displayStatus}`}
        role="status"
        aria-label={label}
        title={label}
        data-testid="transport-status"
      >
        {statusIcon(transport.status)}
        <span>{label}</span>
      </span>
    );
  };

  const renderTruckActions = (transport: TransportRouteSummaryDto): React.ReactNode => (
    <Dropdown
      trigger={['click']}
      menu={{
        items: getRowMenuItems(transport),
        onClick: ({ key }) => {
          if (key === 'view') navigate(withTransportReturnContext(`/app/transports/${transport.id}`, returnContext));
          if (key === 'edit') navigate(withTransportReturnContext(`/app/transports/${transport.id}/edit`, returnContext));
          if (key === 'duplicate') void duplicate(transport.id);
          if (key === 'delete') setPendingDelete(transport);
          if (key.startsWith('status:')) {
            void handleQuickStatusChange(transport, key.slice('status:'.length) as TransportRouteStatus);
          }
        },
      }}
    >
      <Button
        type="text"
        className="transport-truck-row__menu"
        icon={<MoreOutlined />}
        loading={duplicating || updatingStatusId === transport.id}
        disabled={duplicating || updatingStatusId === transport.id}
        aria-label={t('transport:menu.open')}
      />
    </Dropdown>
  );

  const renderSupplierOrders = (transport: TransportRouteSummaryDto): React.ReactNode => {
    if (transport.supplierOrders.length === 0) {
      return <span className="transport-truck-row__empty">{t('transport:grouping.noSupplierOrders')}</span>;
    }

    return (
      <div className="transport-truck-row__suppliers">
        {transport.supplierOrders.map((supplierOrder) => (
          <button
            type="button"
            className="transport-truck-row__supplier-link"
            key={supplierOrder.id}
            onClick={() => navigate(`/app/supplier-orders/${supplierOrder.id}`)}
          >
            <span>{supplierOrder.displayOrderNumber}</span>
            {supplierOrder.supplierName ? <small>{supplierOrder.supplierName}</small> : null}
          </button>
        ))}
      </div>
    );
  };

  const renderProducts = (products: TransportProductDto[]): React.ReactNode => {
    if (products.length === 0) {
      return <span className="transport-truck-row__empty">{t('transport:grouping.warehouseSupply')}</span>;
    }
    const basicLabel = t('products:catalogPicker.basicProduct');

    return (
      <div className="transport-truck-row__products">
        {products.map((product, index) => {
          const presentation = presentProduct(
            product,
            basicLabel,
            i18n.language,
            `${product.name}-${index}`,
          );
          return (
            <div className="transport-product-line" key={presentation.key}>
              {presentation.quantity ? (
                <span className="transport-product-line__quantity">{presentation.quantity}</span>
              ) : null}
              <div className="transport-product-line__identity">
                <strong>{presentation.name}</strong>
                {presentation.dimensions ? <span>{presentation.dimensions}</span> : null}
                {presentation.labels.length > 0 ? <small>{presentation.labels.join(' · ')}</small> : null}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderDetailAddresses = (addresses: string[]): React.ReactNode => (
    addresses.length > 0
      ? (
        <ul className="transport-truck-details__address-list">
          {addresses.map((address, index) => <li key={`${address}-${index}`}>{address}</li>)}
        </ul>
      )
      : <span>—</span>
  );

  const renderTruckDetails = (
    transport: TransportRouteSummaryDto,
    truckKey: string,
  ): React.ReactNode => (
    <div
      className="transport-truck-details"
      id={`transport-truck-details-${truckKey.replace(/[^a-zA-Z0-9_-]/g, '-')}`}
    >
      <div className="transport-truck-details__item">
        <span>{t('transport:fields.loadingAddresses')}</span>
        {renderDetailAddresses(transport.loadingAddresses)}
      </div>
      <div className="transport-truck-details__item">
        <span>{t('transport:fields.deliveryAddresses')}</span>
        {renderDetailAddresses(transport.deliveryAddresses)}
      </div>
      <div className="transport-truck-details__item">
        <span>{t('transport:fields.transportCompany')}</span>
        <strong>{transport.transportCompany || '—'}</strong>
        {transport.transportContact ? <small>{transport.transportContact}</small> : null}
      </div>
      <div className="transport-truck-details__item">
        <span>{t('transport:fields.driver')}</span>
        <strong>{transport.driverName || '—'}</strong>
        {transport.driverPhone ? <small>{transport.driverPhone}</small> : null}
      </div>
      <div className="transport-truck-details__item">
        <span>{t('transport:fields.totalWeight')}</span>
        <strong>{transport.totalWeight == null
          ? '—'
          : `${formatCargoQuantityValue(transport.totalWeight, 'pl-PL')} kg`}</strong>
      </div>
      <div className="transport-truck-details__item">
        <span>{t('transport:fields.totalVolume')}</span>
        <strong>{transport.totalVolume == null
          ? '—'
          : `${formatCargoQuantityValue(transport.totalVolume, 'pl-PL')} m³`}</strong>
      </div>
      <div className="transport-truck-details__item transport-truck-details__item--notes">
        <span>{t('transport:fields.notes')}</span>
        <p>{transport.notes || '—'}</p>
      </div>
    </div>
  );

  const renderTruckRow = (
    transport: TransportRouteSummaryDto,
    products: TransportProductDto[],
    key: string,
    orderId?: string,
  ): React.ReactNode => {
    const expanded = expandedTruckKeys.has(key);
    const detailId = `transport-truck-details-${key.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
    const plates = buildLicensePlates(transport);
    const route = buildTransportRouteLabel(
      transport.loadingAddresses,
      transport.deliveryAddresses,
      transport.originCity,
      transport.destinationCity,
    );
    const metrics = [
      transport.dimensions,
      transport.totalWeight == null
        ? null
        : t('transport:grouping.weightValue', {
          value: formatCargoQuantityValue(transport.totalWeight, 'pl-PL'),
        }),
    ].filter(Boolean);

    return (
      <React.Fragment key={key}>
        <div className={`transport-truck-row transport-truck-row--${getTransportStatusDisplay(transport.status)}`}>
          <div className="transport-truck-row__cell transport-truck-row__identity" data-label={t('transport:grouping.truck')}>
            <button
              type="button"
              className="transport-truck-row__number"
              onClick={() => navigate(withTransportReturnContext(`/app/transports/${transport.id}`, returnContext))}
            >
              {getTransportLabel(transport, t('transport:transport'))}
            </button>
            <TransportDocumentLinks documents={transport.warehouseDocuments} orderId={orderId} />
            <span className="transport-truck-row__plate">
              <CarOutlined aria-hidden /> {plates || t('transport:grouping.noPlate')}
            </span>
            <span className="transport-truck-row__carrier">
              {transport.transportCompany || t('transport:missingTransportCompany')}
            </span>
            <span className="transport-truck-row__status">
              {renderStatus(transport)}
            </span>
            {transport.routeOrders.length > 1 ? (
              <span className="transport-truck-row__shared">
                {t('transport:grouping.sharedTruck', { count: transport.routeOrders.length })}
              </span>
            ) : null}
          </div>

          <div className="transport-truck-row__cell transport-truck-row__cargo" data-label={t('transport:grouping.truckCargo')}>
            {renderProducts(products)}
          </div>

          <div className="transport-truck-row__cell transport-truck-row__supplier-cell" data-label={t('transport:grouping.suppliers')}>
            {renderSupplierOrders(transport)}
          </div>

          <div className="transport-truck-row__cell transport-truck-row__route" data-label={t('transport:grouping.routeAndLoading')}>
            <Tooltip title={<span className="transport-truck-row__route-tooltip">{route.tooltip}</span>}>
              <span className="transport-truck-row__route-label">{route.label}</span>
            </Tooltip>
            <span>
              {t('transport:grouping.loadingDate', {
                date: formatDateEuropean(transport.loadingDate),
              })}
            </span>
            {canViewTransportCosts ? (
              <span className="transport-truck-row__rate">
                {transport.transportRate == null
                  ? t('transport:grouping.noRate')
                  : formatMoneyPl(transport.transportRate, transport.currency || 'PLN')}
              </span>
            ) : null}
          </div>

          <div className="transport-truck-row__cell transport-truck-row__metrics" data-label={t('transport:grouping.truckMetrics')}>
            {metrics.length > 0
              ? metrics.map((metric) => <span key={metric}>{metric}</span>)
              : <span className="transport-truck-row__empty">—</span>}
          </div>

          <div className="transport-truck-row__cell transport-truck-row__actions">
            <Button
              type="text"
              className="transport-truck-row__details-toggle"
              icon={<RightOutlined className={expanded ? 'is-expanded' : ''} />}
              aria-expanded={expanded}
              aria-controls={detailId}
              aria-label={expanded
                ? t('transport:grouping.hideTruckDetails', { number: getTransportLabel(transport) })
                : t('transport:grouping.showTruckDetails', { number: getTransportLabel(transport) })}
              onClick={() => toggleTruckDetails(key)}
            />
            {renderTruckActions(transport)}
          </div>
        </div>
        {expanded ? renderTruckDetails(transport, key) : null}
      </React.Fragment>
    );
  };

  const renderTruckHeader = (): React.ReactNode => (
    <div className="transport-truck-grid-header" aria-hidden="true">
      <span>{t('transport:grouping.truck')}</span>
      <span>{t('transport:grouping.truckCargo')}</span>
      <span>{t('transport:grouping.suppliers')}</span>
      <span>{t('transport:grouping.routeAndLoading')}</span>
      <span>{t('transport:grouping.truckMetrics')}</span>
      <span />
    </div>
  );

  const renderRealization = (group: TransportOrderGroup): React.ReactNode => {
    const productsById = new Map(group.order.products.map((product) => [product.id, product]));
    const basicLabel = t('products:catalogPicker.basicProduct');

    return (
      <div className="transport-order-realization">
        <div className="transport-order-realization__intro">
          <div>
            <h3>{t('transport:grouping.realizationTitle')}</h3>
            <p>{t('transport:grouping.realizationDescription')}</p>
          </div>
        </div>
        <div className="transport-realization-grid transport-realization-grid--header" aria-hidden="true">
          <span>{t('transport:table.product')}</span>
          <span>{t('transport:grouping.ordered')}</span>
          <span>{t('transport:grouping.inRealization')}</span>
          <span>{t('transport:grouping.remaining')}</span>
          <span>{t('transport:grouping.progress')}</span>
        </div>
        {group.productRealization.map((realization) => {
          const product = productsById.get(realization.orderProductId);
          const presentation = product
            ? presentProduct(product, basicLabel, i18n.language, realization.orderProductId)
            : null;
          const unit = formatUnit(realization.unit || product?.unit, i18n.language);
          const quantity = (value: number) => `${formatCargoQuantityValue(value, i18n.language.startsWith('en') ? 'en-US' : 'pl-PL')} ${unit}`;
          const progress = Math.max(0, realization.percent);
          const completed = realization.remainingQuantity <= 0;

          return (
            <div
              className={`transport-realization-grid ${completed ? 'transport-realization-grid--complete' : ''}`}
              key={realization.orderProductId}
            >
              <div className="transport-realization-grid__product" data-label={t('transport:table.product')}>
                <strong>{presentation?.name || realization.name}</strong>
                {presentation?.dimensions || presentation?.labels.length ? (
                  <div className="transport-realization-grid__product-meta">
                    {presentation?.dimensions ? <span>{presentation.dimensions}</span> : null}
                    {presentation?.labels.length ? <small>{presentation.labels.join(' · ')}</small> : null}
                  </div>
                ) : null}
              </div>
              <span data-label={t('transport:grouping.ordered')}>{quantity(realization.orderedQuantity)}</span>
              <span className="transport-realization-grid__allocated" data-label={t('transport:grouping.inRealization')}>
                {quantity(realization.allocatedQuantity)}
              </span>
              <strong className="transport-realization-grid__remaining" data-label={t('transport:grouping.remaining')}>
                {quantity(realization.remainingQuantity)}
              </strong>
              <div className="transport-realization-grid__progress" data-label={t('transport:grouping.progress')}>
                <Progress
                  percent={Math.min(progress, 100)}
                  showInfo={false}
                  size="small"
                  strokeColor={completed ? '#2f855a' : '#1677ff'}
                  trailColor="#e9eef5"
                />
                <span>{formatLocalizedPercent(progress, i18n.language, 1)}</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderOrderGroup = (group: TransportOrderGroup): React.ReactNode => {
    const expanded = expandedOrderIds.has(group.orderId);
    const orderNumber = group.order.displayOrderNumber || group.order.orderNumber;
    const customerName = group.order.customer?.name || group.order.customerName;
    const routes = group.trucks.map(({ transport, routeOrder }) => buildTransportOrderRouteLabel(transport, routeOrder));
    const routeLabels = Array.from(new Set(routes.map((route) => route.label)));
    const hasMultipleRoutes = routeLabels.length > 1;
    const routeTooltip = Array.from(new Set(routes.map((route) => route.tooltip))).join('\n\n');
    const hasMultipleTransports = group.trucks.length > 1;

    return (
      <section
        className={`transport-order-group ${hasMultipleRoutes ? 'transport-order-group--multiple-routes' : ''} ${hasMultipleTransports ? 'transport-order-group--multiple-transports' : ''}`}
        key={group.orderId}
      >
        <header className="transport-order-group__header">
          <button
            type="button"
            className="transport-order-group__toggle"
            onClick={() => toggleOrder(group.orderId)}
            aria-expanded={expanded}
            aria-label={expanded
              ? t('transport:grouping.collapseOrder', { number: orderNumber })
              : t('transport:grouping.expandOrder', { number: orderNumber })}
          >
            <RightOutlined className={expanded ? 'is-expanded' : ''} />
          </button>
          <div className="transport-order-group__fact transport-order-group__fact--route">
            <div className="transport-order-group__route-heading">
              <span>{t('transport:grouping.route')}</span>
              <button
                type="button"
                className="transport-order-group__order-reference-link"
                onClick={() => navigate(`/app/orders/${group.orderId}`)}
              >
                {orderNumber}
              </button>
            </div>
            <Tooltip title={<span className="transport-order-group__route-tooltip">{routeTooltip}</span>}>
              <div className="transport-order-group__route-list">
                {(routeLabels.length > 0 ? routeLabels : ['—']).map((routeLabel) => (
                  <strong key={routeLabel}>{routeLabel === '-' ? t('transport:orderRoutePoints.unassigned') : routeLabel.replace(/\?/g, t('transport:orderRoutePoints.unassigned'))}</strong>
                ))}
              </div>
            </Tooltip>
            <button
              type="button"
              className="transport-order-group__customer-link transport-order-group__route-customer"
              onClick={() => navigate(`/app/orders/${group.orderId}`)}
            >
              {customerName || '—'}
            </button>
          </div>
          <div className="transport-order-group__fact">
            <span>{t('transport:grouping.carrier')}</span>
            <div className="transport-order-group__carrier-list">
              {group.trucks.map(({ transport }) => (
                <div key={transport.id}>
                  <strong>{transport.transportCompany || t('transport:missingTransportCompany')}</strong>
                  {transport.loadingDate ? (
                    <small>{t('transport:grouping.loadingDate', {
                      date: formatDateEuropean(transport.loadingDate),
                    })}</small>
                  ) : null}
                  {renderStatus(transport)}
                </div>
              ))}
            </div>
          </div>
          {canViewTransportCosts ? (
            <div className="transport-order-group__fact transport-order-group__fact--rate">
              <span>{t('transport:grouping.rate')}</span>
              <div className="transport-order-group__rate-list">
                {group.trucks.map(({ transport }) => (
                  <strong key={transport.id}>
                    {transport.transportRate == null
                      ? t('transport:grouping.noRate')
                      : formatMoneyPl(transport.transportRate, transport.currency || 'PLN')}
                  </strong>
                ))}
              </div>
            </div>
          ) : null}
          <div className="transport-order-group__actions">
            {canManageTransports ? (
              <Tooltip title={t('transport:grouping.addTransportForOrder', { customer: customerName || orderNumber })}>
                <Button
                  type="primary"
                  shape="circle"
                  icon={<PlusOutlined />}
                  className="transport-order-group__add-transport"
                  aria-label={t('transport:grouping.addTransportForOrder', { customer: customerName || orderNumber })}
                  onClick={() => navigate(`/app/transports/new?orderId=${encodeURIComponent(group.orderId)}`)}
                />
              </Tooltip>
            ) : null}
          </div>
          {group.trucks.some(({ transport }) => transport.warehouseDocuments !== undefined) ? (
            <div className="transport-order-group__documents">
              {group.trucks.filter(({ transport }) => transport.warehouseDocuments !== undefined).map(({ transport }) => (
                <div className="transport-order-group__document-row" key={transport.id}>
                  {hasMultipleTransports ? (
                    <button
                      type="button"
                      className="transport-order-group__document-truck"
                      onClick={() => navigate(withTransportReturnContext(`/app/transports/${transport.id}`, returnContext))}
                    >
                      {transport.transportCompany || t('transport:missingTransportCompany')}
                      {transport.loadingDate ? ` · ${formatDateEuropean(transport.loadingDate)}` : ''}
                      {buildLicensePlates(transport) ? ` · ${buildLicensePlates(transport)}` : ''}
                    </button>
                  ) : null}
                  <TransportDocumentLinks documents={transport.warehouseDocuments} orderId={group.orderId} />
                </div>
              ))}
            </div>
          ) : null}
        </header>

        {expanded ? (
          <div className="transport-order-group__body">
            {renderRealization(group)}
            <div className="transport-order-group__trucks-title">
              <h3>{t('transport:grouping.assignedTrucks')}</h3>
              <span>{t('transport:grouping.oneTransportOneTruck')}</span>
            </div>
            <div className="transport-trucks-table">
              {renderTruckHeader()}
              {group.trucks.map((truck: TransportOrderTruck) => renderTruckRow(
                truck.transport,
                truck.products,
                `${group.orderId}:${truck.transport.id}`,
                group.orderId,
              ))}
            </div>
          </div>
        ) : null}
      </section>
    );
  };

  const renderSupplierOnlySection = (): React.ReactNode => {
    if (grouping.supplierOnlyTransports.length === 0) return null;

    return (
      <section className="transport-order-group transport-order-group--supplier-only">
        <header className="transport-order-group__header">
          <button
            type="button"
            className="transport-order-group__toggle"
            onClick={() => setSupplierOnlyExpanded((current) => !current)}
            aria-expanded={supplierOnlyExpanded}
            aria-label={supplierOnlyExpanded
              ? t('transport:grouping.collapseSupplierOnly')
              : t('transport:grouping.expandSupplierOnly')}
          >
            <RightOutlined className={supplierOnlyExpanded ? 'is-expanded' : ''} />
          </button>
          <div className="transport-order-group__identity">
            <span className="transport-order-group__eyebrow">{t('transport:grouping.inboundSupply')}</span>
            <h2>{t('transport:grouping.supplierOnlyTitle')}</h2>
            <span className="transport-order-group__customer">
              {t('transport:grouping.supplierOnlyDescription')}
            </span>
          </div>
        </header>
        {supplierOnlyExpanded ? (
          <div className="transport-order-group__body transport-order-group__body--supplier-only">
            {visibleSupplierOnlyRouteSections.map((routeSection) => (
              <section className="transport-supplier-route" key={routeSection.key}>
                <header className="transport-supplier-route__header">
                  <div>
                    <span>{t('transport:grouping.routeSection')}</span>
                    <h3>{routeSection.label === '-'
                      ? t('transport:grouping.routeUnknown')
                      : routeSection.label}</h3>
                  </div>
                  <strong>{t('transport:grouping.routeTrucks', {
                    count: routeSection.transports.length,
                  })}</strong>
                </header>
                <div className="transport-trucks-table">
                  {renderTruckHeader()}
                  {routeSection.transports.map((transport) => renderTruckRow(
                    transport,
                    [],
                    `supplier-only:${transport.id}`,
                  ))}
                </div>
              </section>
            ))}
            {sortedSupplierOnlyTransports.length > pageSize ? (
              <div className="transport-order-groups__pagination">
                <Pagination
                  current={supplierOnlyPage}
                  pageSize={pageSize}
                  total={sortedSupplierOnlyTransports.length}
                  showSizeChanger={false}
                  onChange={setSupplierOnlyPage}
                  showTotal={(total) => t('transport:grouping.paginationTrucks', { total })}
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </section>
    );
  };

  const renderRouteSection = (section: TransportRouteSection): React.ReactNode => {
    const collapsed = !expandedRouteKeys.has(section.key);
    const routeId = `transport-route-section-${encodeURIComponent(section.key)}`;

    return (
      <section className="transport-route-section" key={section.key}>
        <header className="transport-route-section__header">
          <button
            type="button"
            className="transport-route-section__toggle"
            onClick={() => toggleRoute(section.key)}
            aria-expanded={!collapsed}
            aria-controls={routeId}
            aria-label={collapsed
              ? t('transport:grouping.expandRoute', { route: section.label })
              : t('transport:grouping.collapseRoute', { route: section.label })}
          >
            <RightOutlined className={collapsed ? '' : 'is-expanded'} />
          </button>
          <div className="transport-route-section__identity">
            <span className="transport-route-section__eyebrow">
              <NodeIndexOutlined aria-hidden /> {t('transport:grouping.routeSection')}
            </span>
            <Tooltip title={<span className="transport-truck-row__route-tooltip">{section.tooltip}</span>}>
              <h2>{section.label === '-' ? t('transport:grouping.routeUnknown') : section.label}</h2>
            </Tooltip>
          </div>
          <div className="transport-route-section__meta">
            <span>{t('transport:grouping.routeLoadingPoints', { count: section.loadingPointCount })}</span>
            <span>{t('transport:grouping.routeDeliveryPoints', { count: section.deliveryPointCount })}</span>
            <strong>{t('transport:grouping.routeOrdersAndTrucks', {
              orders: section.orderGroups.length,
              trucks: section.truckCount,
            })}</strong>
          </div>
        </header>
        {!collapsed ? (
          <div className="transport-route-section__body" id={routeId}>
            {section.orderGroups.map(renderOrderGroup)}
          </div>
        ) : null}
      </section>
    );
  };

  const renderCompactOrderCell = (transport: TransportRouteSummaryDto): React.ReactNode => {
    if (transport.routeOrders.length === 0) {
      return (
        <span className="transport-compact-table__supplier-orders">
          {transport.supplierOrders.map((supplierOrder) => (
            <button
              type="button"
              key={supplierOrder.id}
              className="transport-compact-table__link"
              onClick={() => navigate(`/app/supplier-orders/${supplierOrder.id}`)}
            >
              {supplierOrder.displayOrderNumber}
            </button>
          ))}
        </span>
      );
    }
    return (
      <span className="transport-compact-table__orders">
        {transport.routeOrders.map((routeOrder) => {
          const number = routeOrder.order.displayOrderNumber || routeOrder.order.orderNumber;
          return (
            <button
              type="button"
              key={routeOrder.id}
              className="transport-compact-table__link"
              onClick={() => navigate(`/app/orders/${routeOrder.orderId}`)}
            >
              {number}
            </button>
          );
        })}
      </span>
    );
  };

  const renderCompactExpandedRow = (transport: TransportRouteSummaryDto): React.ReactNode => (
    <div className="transport-compact-table__expanded">
      <div className="transport-compact-table__expanded-block">
        <span>{t('transport:grouping.truckCargo')}</span>
        {renderProducts(transport.products)}
      </div>
      <div className="transport-compact-table__expanded-block">
        <span>{t('transport:grouping.suppliers')}</span>
        {renderSupplierOrders(transport)}
      </div>
      <div className="transport-compact-table__expanded-block">
        <span>{t('transport:grouping.truckMetrics')}</span>
        {renderTruckDetails(transport, `compact:${transport.id}`)}
      </div>
      <TransportDocumentLinks documents={transport.warehouseDocuments} />
    </div>
  );

  // The flat list leads with the route: the truck number stays in the data and
  // in the row menu, but the operator scans by where the freight goes.
  const compactColumns: ColumnsType<TransportRouteSummaryDto> = [
    {
      title: t('transport:grouping.columns.route'),
      key: 'route',
      fixed: 'left',
      width: 200,
      render: (_, transport) => {
        const route = buildTransportRouteLabel(transport.loadingAddresses,
          transport.deliveryAddresses, transport.originCity, transport.destinationCity);
        return (
          <div className="transport-compact-table__identity">
            <Tooltip title={route.tooltip}><button
              type="button"
              className="transport-compact-table__link transport-compact-table__route-link"
              onClick={() => navigate(withTransportReturnContext(`/app/transports/${transport.id}`, returnContext))}
            >
              {route.label === '-' ? t('transport:grouping.routeUnknown') : route.label}
            </button></Tooltip>
            {renderStatus(transport)}
          </div>
        );
      },
    },
    {
      title: t('transport:grouping.columns.orders'),
      key: 'orders',
      width: 130,
      render: (_, transport) => renderCompactOrderCell(transport),
    },
    {
      title: t('transport:grouping.columns.customer'),
      key: 'customer',
      width: 140,
      render: (_, transport) => {
        const customers = Array.from(new Set(transport.routeOrders.map(({ order }) => (
          order.customer?.name || order.customerName
        )).filter(Boolean)));
        return customers.join(' · ') || transport.customerName || '—';
      },
    },
    ...(canViewTransportCosts ? [{
      title: t('transport:grouping.columns.rate'),
      key: 'rate',
      className: 'transport-compact-table__money',
      align: 'right',
      width: 125,
      render: (transport: TransportRouteSummaryDto) => (transport.transportRate == null
        ? t('transport:grouping.noRate')
        : formatMoneyPl(transport.transportRate, transport.currency || 'PLN')),
    } as const] : []),
    {
      title: t('transport:grouping.columns.dimensions'),
      key: 'dimensions',
      width: 190,
      render: (_, transport) => transport.products.length > 0 ? (
        <div className="transport-compact-table__dimensions">
          {transport.products.map((product, index) => {
            const presentation = presentProduct(product, t('products:catalogPicker.basicProduct'),
              i18n.language, `${transport.id}-${index}`);
            return <div className="transport-compact-table__cargo-line" key={presentation.key}>
              <strong>{presentation.name}</strong>
              <span>{[presentation.dimensions, presentation.quantity].filter(Boolean).join(' · ') || '—'}</span>
              {presentation.labels.length > 0 ? <span>{presentation.labels.join(' · ')}</span> : null}
            </div>;
          })}
        </div>
      ) : '—',
    },
    {
      title: t('transport:grouping.columns.loadingDate'),
      key: 'loadingDate',
      width: 105,
      render: (_, transport) => (transport.loadingDate ? formatDateEuropean(transport.loadingDate) : '—'),
    },
    {
      title: t('transport:grouping.columns.carrier'),
      key: 'carrier',
      width: 140,
      render: (_, transport) => transport.transportCompany || t('transport:missingTransportCompany'),
    },
    {
      title: t('transport:grouping.columns.plates'),
      key: 'plates',
      width: 130,
      render: (_, transport) => buildLicensePlates(transport) || t('transport:grouping.noPlate'),
    },
    {
      title: t('transport:grouping.columns.invoice'),
      key: 'invoice',
      width: 150,
      render: (_, transport) => transport.invoiceNumber || '—',
    },
    {
      title: '',
      key: 'actions',
      fixed: 'right',
      width: 48,
      render: (_, transport) => renderTruckActions(transport),
    },
  ];

  const renderCompactTable = (): React.ReactNode => (
    <div className="transport-compact-workspace" data-testid="transport-compact-table">
      <div className="transport-compact-panel">
        <Table<TransportRouteSummaryDto>
          className="transport-compact-table" size="small" rowKey="id"
          columns={compactColumns} dataSource={compactPageItems}
          scroll={{ x: 1300 }} pagination={false}
          expandable={{ expandedRowRender: renderCompactExpandedRow, columnWidth: 32 }}
          locale={{ emptyText: <Empty description={t('transport:emptyState')} /> }}
          loading={loading && transports.length === 0}
        />
      </div>
      {sortedTransports.length > 0 ? <Pagination
        current={compactPage} pageSize={pageSize} total={sortedTransports.length}
        showSizeChanger pageSizeOptions={[10, 25, 50]}
        onChange={(page, size) => { setCompactPage(size === pageSize ? page : 1); setPageSize(size); }}
        showTotal={(total) => t('transport:grouping.paginationTrucks', { total })}
      /> : null}
    </div>
  );

  const handleListViewChange = (next: TransportListView): void => {
    setListView(next);
    window.localStorage.setItem('transport-routes-list-view', next);
  };

  return (
    <>
      <div className="transport-routes-page">
        <div className="transport-routes-page__workspace-bar">
          <div className="transport-routes-page__view-chips" role="tablist" aria-label={t('transport:transportRoutes')}>
            {(['all', 'inProgress', 'completed', 'planned'] as TransportStatusView[]).map((view) => (
              <button
                key={view}
                type="button"
                role="tab"
                aria-selected={currentStatusView === view}
                className={`transport-routes-page__view-chip ${currentStatusView === view ? 'is-active' : ''}`}
                onClick={() => {
                  if (view === 'all') setStatusFilter([]);
                  else {
                    const group = TRANSPORT_STATUS_VIEW_GROUPS[
                      view as Exclude<TransportStatusView, 'all' | 'custom'>
                    ];
                    if (group) setStatusFilter(group);
                  }
                }}
              >
                {t(`transport:tabs.${view}`)}
              </button>
            ))}
          </div>

          <div className="transport-routes-page__quick-actions">
            <Segmented<TransportListView>
              aria-label={t('transport:grouping.listView')}
              value={listView}
              onChange={handleListViewChange}
              options={[
                {
                  value: 'grouped',
                  icon: <UnorderedListOutlined />,
                  label: t('transport:grouping.viewGrouped'),
                },
                {
                  value: 'compact',
                  icon: <TableOutlined />,
                  label: t('transport:grouping.viewCompact'),
                },
              ]}
            />
            <Input
              className="transport-routes-page__quick-search"
              allowClear
              prefix={<SearchOutlined />}
              placeholder={t('transport:searchPlaceholder')}
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
            {canManageTransports ? (
              <Button
                className="transport-routes-page__create-button"
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate(withTransportReturnContext('/app/transports/new', returnContext))}
              >
                {t('transport:grouping.addTransport')}
              </Button>
            ) : null}
            <Button
              className="transport-routes-page__refresh-button"
              icon={<ReloadOutlined />}
              onClick={() => void fetchTransports()}
            >
              {t('transport:actions.refresh')}
            </Button>
          </div>
        </div>

        <div className="transport-routes-page__list-tools">
          <div className="transport-routes-page__counts">
            <Select<TransportSortMode>
              size="small"
              virtual={false}
              className="transport-routes-page__sort"
              aria-label={t('transport:grouping.sortLabel')}
              value={sortMode}
              onChange={setSortMode}
              options={[
                { value: 'source', label: t('transport:grouping.sortSource') },
                { value: 'orderNumber', label: t('transport:grouping.sortOrderNumber') },
                { value: 'orderNumberDescending', label: t('transport:grouping.sortOrderNumberDescending') },
                { value: 'customer', label: t('transport:grouping.sortCustomer') },
                { value: 'customerDescending', label: t('transport:grouping.sortCustomerDescending') },
                { value: 'loadingNewest', label: t('transport:grouping.sortLoadingNewest') },
                { value: 'loadingOldest', label: t('transport:grouping.sortLoadingOldest') },
                { value: 'carrier', label: t('transport:grouping.sortCarrier') },
                { value: 'carrierDescending', label: t('transport:grouping.sortCarrierDescending') },
                ...(canViewTransportCosts ? [
                  { value: 'rateHighest' as TransportSortMode, label: t('transport:grouping.sortRateHighest') },
                  { value: 'rateLowest' as TransportSortMode, label: t('transport:grouping.sortRateLowest') },
                ] : []),
                { value: 'status', label: t('transport:grouping.sortStatus') },
                { value: 'statusDescending', label: t('transport:grouping.sortStatusDescending') },
              ]}
            />
            <span>{t('transport:grouping.listSummary', {
              orders: grouping.orderGroups.length,
              trucks: transports.length,
            })}</span>
          </div>
        </div>

        <div className="transport-order-groups" aria-busy={loading}>
          {listView === 'compact' ? renderCompactTable() : (
            <>
              {loading && transports.length === 0 ? (
                <div className="transport-order-groups__loading"><Spin size="large" /></div>
              ) : null}
              {!loading && transports.length === 0 ? (
                <div className="transport-order-groups__empty">
                  <Empty description={t('transport:emptyState')} />
                </div>
              ) : null}
              {visibleRouteSections.map(renderRouteSection)}
              {renderSupplierOnlySection()}
            </>
          )}
          {listView === 'grouped' && sortedOrderGroups.length > pageSize ? (
            <div className="transport-order-groups__pagination">
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={sortedOrderGroups.length}
                showSizeChanger
                pageSizeOptions={[10, 25, 50]}
                onChange={(page, nextPageSize) => {
                  if (nextPageSize !== pageSize) setPageSize(nextPageSize);
                  setCurrentPage(page);
                }}
                showTotal={(total) => t('transport:grouping.paginationOrders', { total })}
              />
            </div>
          ) : null}
        </div>
      </div>

      {deliveryTransport && (
        <TransportDeliveryConfirmationModal key={deliveryTransport.id} transport={deliveryTransport}
          onCancel={() => setDeliveryTransport(null)} onConfirm={confirmDelivery} />
      )}
      <Modal
        title={t('transport:confirmDelete')}
        open={Boolean(pendingDelete)}
        onOk={() => void handleDelete()}
        onCancel={() => setPendingDelete(null)}
        okButtonProps={{ danger: true }}
        okText={t('common:delete')}
        cancelText={t('common:cancel')}
      >
        <p>{pendingDelete && getTransportLabel(pendingDelete)
          ? t('transport:messages.deleteConfirmTitle', { number: getTransportLabel(pendingDelete) })
          : t('transport:confirmDeleteContent')}</p>
      </Modal>
    </>
  );
};

export default TransportRoutes;
